<style lang="scss">
  @use "../../../assets/css/design-system/colors";
  @use "../../../assets/css/design-system/utils";

  tr {
    &.head-row {
      height: utils.in_rem(36px);
      border-bottom: 1px solid colors.get(interfaceSwGrey_10);
    }

    &.border-bottom {
      border-bottom: 1px solid colors.get(interfaceSwGrey_5);
    }

    &.hidden {
      display: none;
    }

    &.has-children {
      cursor: pointer;
    }

    // Use a custom hover class to handle hover state of children
    &.hover {
      @include colors.background-color(interfaceTransparentBlack_1);
    }
  }
</style>

<script>
  import { getContext, onDestroy, onMount, setContext } from "svelte";
  // Using crypto.randomUUID() instead of uuidV4
  import { DATA_ROW_ID, IS_HEAD, TABLE_STORES } from "./table-context";

  import ExpandRowButton from "./ExpandRowButton.svelte";
  import TD from "./TD.svelte";

  export let expanded = false;
  export let isChild = false;
  export let onMountCallback = null;
  export let style = "";
  export let onclick = null;
  export let onkeydown = null;
  export let role = undefined;
  export let tabindex = undefined;
  export { className as class };
  let className = "";

  const dataRowId = crypto.randomUUID();
  setContext(DATA_ROW_ID, dataRowId);

  const isHead = getContext(IS_HEAD) || false;

  /** @type {HTMLElement | null} */
  let el = null;

  const {
    rowSiblingsStore,
    isChildStore,
    rowVisibleStore,
    hasExpandButtonColumnStore,
    expandButtonStateStore,
    childrenStore,
    lastParentStore,
    lastChildStore,
    hoveredParentStore,
  } = getContext(TABLE_STORES);

  $: hidden = !$rowVisibleStore[dataRowId];
  $: $isChildStore[dataRowId] = isChild;
  $: expanded = $isChildStore[dataRowId]
    ? $rowVisibleStore[dataRowId]
    : $expandButtonStateStore[dataRowId];
  $: isParent = !$isChildStore[dataRowId];
  $: hasChildren = $childrenStore[dataRowId]?.length > 0;

  // Add bottom border to non-expanded parents except the last one
  // Also add bottom border to the last child unless it's a child of the last parent
  $: borderBottom =
    (isParent && !expanded && $lastParentStore !== dataRowId) ||
    (isChild &&
      $lastChildStore[dataRowId] &&
      !$childrenStore[$lastParentStore]?.includes(dataRowId));

  // Highlight parent + children when hovering over a parent row
  $: hover =
    $hoveredParentStore === dataRowId ||
    ($hoveredParentStore &&
      $childrenStore[$hoveredParentStore]?.includes(dataRowId));

  const handleClick = (event) => {
    // Call external onclick if provided
    if (onclick) {
      onclick(event);
    }

    // Handle row expansion
    if (!hasChildren) return;
    $expandButtonStateStore[dataRowId] = !$expandButtonStateStore[dataRowId];
  };

  const onmouseenter = () => {
    if (!hasChildren) return;
    $hoveredParentStore = dataRowId;
  };

  const onmouseleave = () => {
    if (!hasChildren) return;
    $hoveredParentStore = null;
  };

  const next = () =>
    el.nextElementSibling?.attributes.getNamedItem("data-row-id")?.value;
  const prev = () =>
    el.previousElementSibling?.attributes.getNamedItem("data-row-id")?.value ||
    "first";

  let mounted = false;
  $: {
    // hot code path for large tables => performance sensitive
    // => param re-assign to prevent copy.

    if (el && mounted) {
      rowSiblingsStore.update(($rowSiblingsStore) => {
        $rowSiblingsStore[prev()] = dataRowId;
        $rowSiblingsStore[dataRowId] = next();
        return $rowSiblingsStore;
      });
    } else if (el) {
      rowSiblingsStore.update(($rowSiblingsStore) => {
        $rowSiblingsStore[prev()] = next();
        delete $rowSiblingsStore[dataRowId];
        return $rowSiblingsStore;
      });
    }
  }

  onMount(() => {
    mounted = true;
    if (onMountCallback) {
      return onMountCallback(dataRowId);
    }
    return null;
  });

  onDestroy(() => {
    mounted = false;
  });
</script>

{#if isHead}
  <tr class="head-row">
    <slot />
    <!-- If expandable, add an empty head cell for the expand button -->
    {#if $hasExpandButtonColumnStore}
      <TD />
    {/if}
  </tr>
{:else}
  <tr
    bind:this={el}
    class={[
      className,
      borderBottom && "border-bottom",
      hidden && "hidden",
      hasChildren && "has-children",
      hover && "hover",
    ]}
    on:click={handleClick}
    on:mouseenter={onmouseenter}
    on:mouseleave={onmouseleave}
    on:keydown={onkeydown}
    data-row-id={dataRowId}
    {style}
    {role}
    {tabindex}
    {...$$restProps}
  >
    {#if $rowVisibleStore[dataRowId]}
      <slot />
      {#if hasChildren}
        <!-- If expandable, add an expand button cell -->
        <TD>
          <ExpandRowButton {dataRowId} />
        </TD>
      {:else if $hasExpandButtonColumnStore}
        <!-- If not expandable, add an empty cell for alignment, as long as at least one other row has expandable cells -->
        <TD />
      {/if}
    {/if}
  </tr>
{/if}
